import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { useEditorChrome } from '@/components/app-editor/use-editor-chrome';
import {
  VisualEditorLayout,
  type PublishState,
} from '@/components/app-editor/visual-editor-layout';
import { WelcomeDelayPicker } from '@/components/welcome-notification/welcome-delay-picker';
import { WelcomeStudioPreview } from '@/components/welcome-notification/welcome-studio-preview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { Textarea } from '@/components/ui/textarea';
import { useBlockerFull } from '@/hooks/use-blocker-full';
import { useTenant } from '@/hooks/use-tenant';
import {
  useGetWelcomeNotification,
  useSetWelcomeNotification,
} from '@/hooks/use-welcome-notification';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  isWelcomeFormDirty,
  validateWelcomeForm,
  welcomeDtoToForm,
  welcomeFormToDto,
  type WelcomeFieldError,
  type WelcomeNotificationForm,
} from '@/lib/app-editor/welcome-notification-form';
import type { WelcomeNotificationDto } from '@/lib/dtos';

type TabId = 'message' | 'link' | 'when';

export default function WelcomeNotificationPage() {
  const { LL } = useI18nContext();
  const { data, isPending, isError, refetch } = useGetWelcomeNotification();

  if (isError) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 p-6">
        <p className="text-sm text-muted-foreground">{LL.appHub.loadError()}</p>
        <Button variant="outline" onClick={() => void refetch()}>
          {LL.appHub.retry()}
        </Button>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader2 aria-hidden className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <WelcomeNotificationEditor saved={data} />;
}

/** `saved` is the API's answer: `null` when nothing is configured (200 without body). */
function WelcomeNotificationEditor({
  saved,
}: {
  saved: WelcomeNotificationDto | null;
}) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const chrome = useEditorChrome<TabId>('message');
  const { mutateAsync, isPending } = useSetWelcomeNotification();

  const [baseline, setBaseline] = useState(() => welcomeDtoToForm(saved));
  const [form, setForm] = useState(baseline);
  const [showErrors, setShowErrors] = useState(false);
  const [justPublished, setJustPublished] = useState(false);

  const errors = validateWelcomeForm(form, tenant.storeWebsiteDomain);
  const dirty = isWelcomeFormDirty(baseline, form);
  useBlockerFull(dirty && !isPending);

  const errorMessage: Record<WelcomeFieldError, string> = {
    titleRequired: LL.createCampaign.validation.notificationTitleRequired(),
    bodyRequired: LL.createCampaign.validation.notificationBodyRequired(),
    linkRequired: LL.createCampaign.validation.notificationLinkRequired(),
    domainNotConfigured:
      LL.createCampaign.validation.websiteDomainNotConfigured(),
    linkPrefix: LL.createCampaign.validation.linkMustStartWith({
      prefix: `https://${tenant.storeWebsiteDomain ?? ''}`,
    }),
  };

  const update = (next: WelcomeNotificationForm) => {
    setForm(next);
    setJustPublished(false);
  };

  const publishState: PublishState = isPending
    ? 'pending'
    : dirty
      ? 'dirty'
      : justPublished
        ? 'published'
        : 'clean';

  const messageHasError = Boolean(errors.title ?? errors.body);
  const linkHasError = Boolean(errors.link);

  const publish = async () => {
    if (messageHasError || linkHasError) {
      setShowErrors(true);
      chrome.focusTarget(messageHasError ? 'message' : 'link');
      return;
    }
    try {
      await mutateAsync(welcomeFormToDto(form));
      setBaseline(form);
      setJustPublished(true);
      toast.success(LL.appEditor.published());
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : LL.appEditor.publishFailed()
      );
    }
  };

  const fieldError = (field: 'title' | 'body' | 'link') => {
    const error = errors[field];
    return showErrors && error ? (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage[error]}
      </p>
    ) : null;
  };

  return (
    <VisualEditorLayout<TabId>
      title={LL.appCustomizationPage.welcomeNotifications.title()}
      backTo="../"
      chrome={chrome}
      active={{
        checked: form.isActive,
        onCheckedChange: (isActive) => update({ ...form, isActive }),
        label: LL.appEditor.active(),
      }}
      publish={{ state: publishState, onPublish: () => void publish() }}
      tabs={[
        {
          id: 'message',
          label: LL.welcomeEditor.tabs.message(),
          hasError: showErrors && messageHasError,
          content: (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="welcome-title">
                  {LL.createCampaign.notificationTitleLabel()}
                </Label>
                <Input
                  id="welcome-title"
                  value={form.title}
                  aria-invalid={(showErrors && !!errors.title) || undefined}
                  placeholder={LL.appCustomizationPage.welcomeNotifications.notificationTitlePlaceholder()}
                  onChange={(e) => update({ ...form, title: e.target.value })}
                />
                {fieldError('title')}
              </div>
              <div className="space-y-2">
                <Label htmlFor="welcome-body">
                  {LL.createCampaign.notificationBodyLabel()}
                </Label>
                <Textarea
                  id="welcome-body"
                  value={form.body}
                  aria-invalid={(showErrors && !!errors.body) || undefined}
                  placeholder={LL.appCustomizationPage.welcomeNotifications.notificationBodyPlaceholder()}
                  onChange={(e) => update({ ...form, body: e.target.value })}
                />
                {fieldError('body')}
              </div>
            </div>
          ),
        },
        {
          id: 'link',
          label: LL.welcomeEditor.tabs.link(),
          hasError: showErrors && linkHasError,
          content: (
            <div className="space-y-2">
              <Label htmlFor="welcome-link">
                {LL.createCampaign.notificationLinkLabel()}
              </Label>
              <Input
                id="welcome-link"
                value={form.link}
                aria-invalid={(showErrors && !!errors.link) || undefined}
                placeholder={LL.createCampaign.notificationLinkPlaceholder({
                  domain: tenant.storeWebsiteDomain ?? 'your-store.com',
                })}
                onChange={(e) => update({ ...form, link: e.target.value })}
              />
              {fieldError('link')}
              <p className="text-sm text-muted-foreground">
                {LL.createCampaign.notificationLinkDescription()}
              </p>
            </div>
          ),
        },
        {
          id: 'when',
          label: LL.welcomeEditor.tabs.when(),
          content: (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {LL.appCustomizationPage.welcomeNotifications.delayDescription()}
              </p>
              <WelcomeDelayPicker
                value={form.delayMinutes}
                onChange={(delayMinutes) => update({ ...form, delayMinutes })}
              />
            </div>
          ),
        },
      ]}
      canvas={
        <WelcomeStudioPreview
          title={form.title}
          body={form.body}
          iconUrl={tenant.iconUrl}
          locale={locale}
          delayMinutes={form.delayMinutes}
          active={form.isActive}
          focus={chrome.activeTab === 'when' ? 'clock' : 'notification'}
          onNotificationPress={() => chrome.focusTarget('message')}
        />
      }
    />
  );
}
